import { useAsyncExecutor, useAsyncExecutor2 } from "async-executor-hook";
import { differenceInDays, format } from "date-fns";
import { stringify } from "query-string";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  FormControl,
  FormGroup,
  useFormConfig,
  Validators,
  WForm,
  WFormControl,
} from "rectangular-forms";
import {
  catchError,
  distinctUntilChanged,
  from,
  interval,
  map,
  Observable,
  of,
  skip,
  startWith,
  Subject,
  switchMap,
  takeUntil,
} from "rxjs";
import {
  inputMaxLengthFn,
  onlyDigits,
  WCalendar,
  WDropdown,
  WInputText,
  WInputTextDamPrefix,
  WMultipleTagInput,
  WMultipleTagInputAlphaNum,
} from "../../common/components/TemplateContainer/InputTemplates";
import { ADMIN_ROUTES } from "../../utils/routeConstants";
import { AdminReportSidebar } from "./AdminReportSidebar";
import { ButtonReload } from "../../common/components/TemplateContainer/InputTemplateStyles";
import {
  CITATION_REPORT,
  DAM_MAX_LENGTH,
  DD_MM_YY,
  FLYERS_REPORT,
  INTERVAL_30_SECONDS,
  NULL,
  PROCESSING,
  RUC_MAX_LENGTH,
  SUCCESS,
  TRACKING_REPORT,
  YYYY_MM_DD,
} from "../../utils/valueStaticConstants";
import { getDateAttribute } from "../../common/searchParams.utils";
import { getErrorValueResponse } from "../../common/error/Error";
import { ModalGenerateReport } from "./ModalGenerateReport/ModalGenerateReport";
import { ModalProcessingTalma } from "../../common/components/ModalProcessingTalma/ModalProcessingTalma";
import { ModalReportGenerate } from "./ModalReportGenerate/ModalReportGenerate";
import { removeNullProps, removeNullUndefinedProps } from "../../common/utils";
import { SaveButton } from "../../common/components/TemplateContainer/SaveButton";
import { TemplateInputContainer } from "../../common/components/TemplateContainer/TemplateContainer";
import DocumentManagementHeader from "../DocumentManagementHeader/DocumentManagementHeader";
import IconDownload from "../../images/icon_download_report.svg";
import IconDownloadHistory from "../../images/icon_download_history.svg";
import IconVectorRightBlue from "../../images/icon_button_users_blue.svg";
import Reload from "../../images/ic_reload.svg";
import {
  ButtonContent,
  HeaderTitle,
  MainHeaderTitle,
} from "./AdminReportStyle";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import { networkBackendRequest } from "../../utils/networkUtils";
import { DATE_SELECT, HTTP_METHODS } from "../../utils/constants";
import { RequestState } from "../../utils/request.model";
import { ReportCountConvertResponse } from "./ReportConvertResponse";
import { addDateInDays } from "../../utils/DatesFns";
import { Authorization } from "../../common/AuthorizationCpt";
import { ReportAdminParams } from "./interfaces/Report.interfaces";
import { StatusType } from "./AdminReportResource";
import {
  REPORT_CREATE_APPOINTMENTS_REPORT,
  REPORT_CREATE_VOLANTES_REPORT,
  REPORT_GET_APPOINTMENTS_REPORT,
  REPORT_GET_REPORTS,
  REPORT_GET_VOLANTES_REPORT,
} from "../../utils/permissionConstant";

export const REPORT_TYPE_OPTION = [
  {
    value: FLYERS_REPORT,
    label: "Reporte volantes",
    endpoint: "/reports/volantes/",
  },
  {
    value: CITATION_REPORT,
    label: "Reporte citas y turnos",
    endpoint: "/reports/appointments/",
  },
  // TODO: FOR RQ NEXT FOR INTEGRATE WITH BACKEND WHEN ENDPOINT THIS READY
  // {
  //   value: TRACKING_REPORT,
  //   label: "Reporte trazabilidad",
  //   endpoint: "/timeslots-by-date/",
  // },
];

export const REGIMEN_OPTION = [{ value: "10", label: "10 Imp. Definitiva" }];

type CounterState = RequestState<any>;

interface Props {
  params$: Observable<ReportAdminParams>;
  reportChangeState: (report: string) => void;
  loadReportExecutor: any;
  statusEndpoint: StatusType;
}
export const AdminReportSearch = (props: Props) => {
  const history = useHistory();
  const { params$, reportChangeState, loadReportExecutor, statusEndpoint } =
    props;
  const [showSidebarDownload, setShowSidebarDownload] = useState(false);

  const formConfig = useFormConfig({
    createForm: (data, oldForm, formConfig) => {
      const reportType = new FormControl();
      const form = new FormGroup({
        reportType,
        agent_ruc: new FormControl(),
        from_date: new FormControl(null, [Validators.required]),
        id_pedidos: new FormControl(),
        year_dam: new FormControl(),
        num_dam: new FormControl(),
        regimen_dam: new FormControl(),
        to_date: new FormControl(null, [Validators.required]),
        volante_nums: new FormControl(),
        tci_nums: new FormControl(),
      });

      reportType.valueChanges
        .pipe(skip(1), distinctUntilChanged())
        .subscribe((reportType) => {
          formConfig.reset();
          const formValueReload = {
            agent_ruc: "",
            num_dam: "",
            volante_nums: [],
            id_pedidos: [],
            tci_nums: [],
            from_date: undefined,
            to_date: undefined,
            reportType,
            year_dam: new Date().getFullYear().toString(),
            regimen_dam: "10",
          };
          if (reportType === FLYERS_REPORT) {
            reportChangeState(FLYERS_REPORT);
            form.patchValue({
              ...formValueReload,
            });
          }
          if (reportType === CITATION_REPORT) {
            reportChangeState(CITATION_REPORT);
            form.patchValue({
              ...formValueReload,
            });
          }
          if (reportType === TRACKING_REPORT) {
            reportChangeState(TRACKING_REPORT);
            form.patchValue({
              ...formValueReload,
            });
          }
        });
      form.get("from_date").valueChanges.subscribe((from_date: Date) => {
        const toDate = new Date(form.get("to_date").value) || new Date();
        const diffDate = differenceInDays(toDate, new Date(from_date));
        if (diffDate < 1) {
          form.get("to_date").setValue(from_date);
        }
      });

      form.get("to_date").valueChanges.subscribe((to_date: Date) => {
        const toDate = new Date(form.get("from_date").value) || new Date();
        const diffDate = differenceInDays(new Date(to_date), toDate);
        if (diffDate < 1) {
          form.get("from_date").setValue(to_date);
        }
      });

      form.patchValue(data);
      return form;
    },
    onSubmit: (form) => {
      const data = form.value;
      if (
        !form.valid &&
        (data.reportType === FLYERS_REPORT ||
          data.reportType === CITATION_REPORT)
      ) {
        return;
      }
      const from_date = data.from_date && format(data.from_date, YYYY_MM_DD);
      const to_date = data.from_date && format(data.to_date, YYYY_MM_DD);
      const params = removeNullProps({
        ...data,
        from_date,
        to_date,
      });
      const pathname = history.location.pathname;

      history.push(`${pathname}?${stringify(params)}`);
    },
  });

  useAsyncExecutor2((params) => params, {
    params$,
    onSuccess: (params: any) => {
      const date_from = getDateAttribute(params.from_date);
      const to_date = getDateAttribute(params.to_date);
      formConfig.loadSucceed({
        ...params,
        from_date: params.from_date && date_from,
        to_date: params.to_date && to_date,
      });
    },
  });

  const reportType = formConfig.form?.get("reportType")?.value;
  const flyersReportSelected = reportType === FLYERS_REPORT;
  const citationReportSelected = reportType === CITATION_REPORT;
  const trackingReportSelected = reportType === TRACKING_REPORT;

  const [damYearOptions] = useState(() => {
    return DATE_SELECT;
  });

  const damYear = formConfig.form?.get("year_dam").value;
  const damRegimen = formConfig.form?.get("regimen_dam").value;

  const [showModalCreateReport, setShowModalCreateReport] = useState(false);
  const [showModalReportGenerate, setShowModalReportGenerate] = useState(false);
  const [showMessageError, setShowMessageError] = useState("");

  const onChangeState = (stateModalGenerate: boolean) => {
    setShowModalReportGenerate(stateModalGenerate);
  };

  const createReportExecutor = useAsyncExecutor(
    (params: ReportAdminParams) => {
      const { agent_ruc, num_dam } = params;

      const dam_num =
        num_dam &&
        num_dam.length === 6 &&
        `235-${params.year_dam}-${params.regimen_dam}-${params.num_dam}`;
      const from_date = format(params.from_date, YYYY_MM_DD);
      const to_date = format(params.to_date, YYYY_MM_DD);

      const volante_nums =
        params.volante_nums.length > 0 ? params.volante_nums : undefined;
      const tci_nums = params.tci_nums.length > 0 ? params.tci_nums : undefined;
      const id_pedidos =
        params.id_pedidos.length > 0 ? params.id_pedidos : undefined;

      const paramsVolanteAppointmentCommon = {
        agent_ruc,
        dam_num,
        volante_nums,
        tci_nums,
        from_date,
        to_date,
      };
      const paramsVolanteBody = {
        ...paramsVolanteAppointmentCommon,
        id_pedidos,
      };
      if (params.reportType === FLYERS_REPORT) {
        const cleanedParamsVolante =
          removeNullUndefinedProps(paramsVolanteBody);
        return networkBackendRequest(
          "/reports/volantes/create-report/",
          HTTP_METHODS.POST,
          undefined,
          {
            ...cleanedParamsVolante,
          }
        ).then((resp) => resp.json());
      }

      if (params.reportType === CITATION_REPORT) {
        const cleanedParamsAppointment = removeNullUndefinedProps(
          paramsVolanteAppointmentCommon
        );
        return networkBackendRequest(
          "/reports/appointments/create-report/",
          HTTP_METHODS.POST,
          undefined,
          {
            ...cleanedParamsAppointment,
          }
        ).then((resp) => resp.json());
      }
    },
    {
      onSuccess: (data) => {
        setShowModalReportGenerate(true);
      },
      onError: (error) => {
        const errorMessage = getErrorValueResponse(error);
        setShowMessageError(errorMessage);
      },
    }
  );
  const captureErrorSidebar = (captureError: string) => {
    setShowMessageError(captureError);
  };

  const disabledButtonCreateDownload =
    statusEndpoint === NULL
      ? true
      : (loadReportExecutor.status === PROCESSING ||
          loadReportExecutor.status === SUCCESS) &&
        loadReportExecutor.data.count === 0
      ? true
      : false;

  const [countState, setCountState] = useState<CounterState>({});

  const destroyReport$Ref = useRef(new Subject<void>());
  const destroyReport$ = destroyReport$Ref.current;

  const loadCountReport$Ref = useRef(new Subject<void>());
  const loadCountReport$ = loadCountReport$Ref.current;

  useEffect(() => {
    loadCountReport$
      .pipe(
        takeUntil(destroyReport$),
        startWith(null),
        switchMap(() => {
          const request = networkBackendRequest(
            `/reports/track-status/`,
            HTTP_METHODS.GET,
            undefined,
            undefined
          )
            .then((response) => response.json())
            .then(ReportCountConvertResponse);
          return from(request).pipe(
            map((data) => {
              return { status: SUCCESS, data };
            }),
            catchError((error) => {
              return of({ status: "FAILED", error });
            })
          );
        })
      )
      .subscribe((countState) => {
        setCountState(countState as any);
      });
  }, [destroyReport$, loadCountReport$]);

  useEffect(() => {
    interval(INTERVAL_30_SECONDS)
      .pipe(takeUntil(destroyReport$))
      .subscribe(() => {
        loadCountReport$.next();
      });
  }, [loadCountReport$, destroyReport$]);

  useEffect(() => {
    return () => {
      destroyReport$.next();
      destroyReport$.complete();
    };
  }, [destroyReport$]);

  const from_date_add_five = addDateInDays(new Date(), 5);
  return (
    <div>
      <MainHeaderTitle>
        <div>
          <DocumentManagementHeader
            title={ADMIN_ROUTES.REPORTS.title.toUpperCase()}
          />
        </div>
        <HeaderTitle>
          <Authorization
            actions={[
              REPORT_CREATE_VOLANTES_REPORT,
              REPORT_CREATE_APPOINTMENTS_REPORT,
            ]}
          >
            <ButtonContent
              onClick={() => {
                setShowModalCreateReport(true);
              }}
              backgroundColor="#76BC21"
              width="20rem"
              disabled={disabledButtonCreateDownload}
            >
              {"Generar reporte"}
              <img src={IconDownload} alt="" />
            </ButtonContent>
          </Authorization>
          <Authorization actions={[REPORT_GET_REPORTS]}>
            <ButtonContent
              onClick={() => {
                setShowSidebarDownload(true);
              }}
              backgroundColor="#FFFFFF"
              color="#76BC21"
              border="solid"
              width="20rem"
              marginLeft="1.3rem"
              borderWidth="1px"
            >
              {"Ver descargas"}
              {countState.status === SUCCESS &&
                countState.data.report_data_count.length > 0 && (
                  <div className="notification-circle"></div>
                )}
              <img src={IconDownloadHistory} alt="" />
            </ButtonContent>
          </Authorization>
        </HeaderTitle>
      </MainHeaderTitle>
      <WForm formConfig={formConfig}>
        <div
          className="p-grid"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <div className="p-col-12 p-md-3">
            <WFormControl name="reportType">
              <TemplateInputContainer label="Tipo de Reporte:">
                <WDropdown width="100%" options={REPORT_TYPE_OPTION} />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <Authorization actions={[REPORT_GET_VOLANTES_REPORT]}>
            {flyersReportSelected && (
              <>
                <div className="p-col-6 p-md-4">
                  <WFormControl name="agent_ruc">
                    <TemplateInputContainer label="RUC Agente Aduanero:">
                      <WInputText
                        width="100%"
                        placeholder="Ingresa RUC de la empresa"
                        maxLength={RUC_MAX_LENGTH}
                        onInput={inputMaxLengthFn}
                        onKeyPress={onlyDigits}
                      />
                    </TemplateInputContainer>
                  </WFormControl>
                </div>
                <div className="p-col-12 p-md-1">
                  <WFormControl name="year_dam">
                    <TemplateInputContainer label="Año:">
                      <WDropdown width="100%" options={damYearOptions} />
                    </TemplateInputContainer>
                  </WFormControl>
                </div>
                <div className="p-col-12 p-md-2">
                  <WFormControl name="regimen_dam">
                    <TemplateInputContainer label="Régimen:" color="#08082B">
                      <WDropdown width="100%" options={REGIMEN_OPTION} />
                    </TemplateInputContainer>
                  </WFormControl>
                </div>
                <div className="p-col-12 p-md-2">
                  <WFormControl
                    name="num_dam"
                    errorMessages={{
                      minlength: "La dam debe de tener 6 dígitos",
                    }}
                  >
                    <TemplateInputContainer label="Número DAM:" color="#08082B">
                      <WInputTextDamPrefix
                        width="100%"
                        placeholder="123456"
                        damYear={damYear}
                        damRegimenId={damRegimen}
                        maxLength={DAM_MAX_LENGTH}
                        onInput={inputMaxLengthFn}
                        onKeyPress={onlyDigits}
                      />
                    </TemplateInputContainer>
                  </WFormControl>
                </div>
                <div className="p-col-12 p-md-4">
                  <WFormControl name="volante_nums">
                    <TemplateInputContainer label="Volante(s):">
                      <WMultipleTagInput
                        width="100%"
                        placeholder="Ingresa Nº volante(s)"
                        TagInputWidth
                      />
                    </TemplateInputContainer>
                  </WFormControl>
                </div>
                <div className="p-col-12 p-md-4">
                  <WFormControl name="id_pedidos">
                    <TemplateInputContainer label="Pedido(s):">
                      <WMultipleTagInputAlphaNum
                        placeholder="Ingresa Nº pedidos(s)"
                        height="5rem"
                      />
                    </TemplateInputContainer>
                  </WFormControl>
                </div>
                <div className="p-col-12 p-md-2">
                  <WFormControl
                    name="from_date"
                    errorMessages={{
                      required: "*Campo requerido",
                    }}
                  >
                    <TemplateInputContainer
                      label="Fecha desde:"
                      color="#08082B"
                    >
                      <WCalendar
                        width="100%"
                        dateFormat={DD_MM_YY}
                        maxDate={new Date(from_date_add_five)}
                      />
                    </TemplateInputContainer>
                  </WFormControl>
                </div>
                <div className="p-col-12 p-md-2">
                  <WFormControl
                    name="to_date"
                    errorMessages={{
                      required: "*Campo requerido",
                    }}
                  >
                    <TemplateInputContainer
                      label="Fecha hasta:"
                      color="#08082B"
                    >
                      <WCalendar
                        width="100%"
                        maxDate={new Date(from_date_add_five)}
                        dateFormat={DD_MM_YY}
                      />
                    </TemplateInputContainer>
                  </WFormControl>
                </div>
                <div className="p-col-12 p-md-10">
                  <WFormControl name="tci_nums">
                    <TemplateInputContainer label="Nro Serie(s) Factura(s):">
                      <WMultipleTagInputAlphaNum
                        height="5rem"
                        placeholder="Ingresa Nº serie(s) factura(s)"
                        alphaNum
                        numInvoice
                      />
                    </TemplateInputContainer>
                  </WFormControl>
                </div>
              </>
            )}
          </Authorization>

          <Authorization actions={[REPORT_GET_APPOINTMENTS_REPORT]}>
            {citationReportSelected && (
              <>
                <div className="p-col-12 p-md-4">
                  <WFormControl name="agent_ruc">
                    <TemplateInputContainer label="RUC Agente Aduanero:">
                      <WInputText
                        width="100%"
                        placeholder="Ingresa RUC de la empresa"
                        maxLength={RUC_MAX_LENGTH}
                        onInput={inputMaxLengthFn}
                        onKeyPress={onlyDigits}
                      />
                    </TemplateInputContainer>
                  </WFormControl>
                </div>
                <div className="p-col-12 p-md-1">
                  <WFormControl name="year_dam">
                    <TemplateInputContainer label="Año:">
                      <WDropdown width="100%" options={damYearOptions} />
                    </TemplateInputContainer>
                  </WFormControl>
                </div>
                <div className="p-col-12 p-md-2">
                  <WFormControl name="regimen_dam">
                    <TemplateInputContainer label="Régimen:" color="#08082B">
                      <WDropdown width="100%" options={REGIMEN_OPTION} />
                    </TemplateInputContainer>
                  </WFormControl>
                </div>
                <div className="p-col-12 p-md-2">
                  <WFormControl
                    name="num_dam"
                    errorMessages={{
                      minlength: "La dam debe de tener 6 dígitos",
                    }}
                  >
                    <TemplateInputContainer label="Número DAM:" color="#08082B">
                      <WInputTextDamPrefix
                        width="100%"
                        placeholder="123456"
                        damYear={damYear}
                        damRegimenId={damRegimen}
                        maxLength={DAM_MAX_LENGTH}
                        onInput={inputMaxLengthFn}
                        onKeyPress={onlyDigits}
                      />
                    </TemplateInputContainer>
                  </WFormControl>
                </div>
                <div className="p-col-12 p-md-3">
                  <WFormControl name="volante_nums">
                    <TemplateInputContainer label="Volante(s):">
                      <WMultipleTagInput
                        width="100%"
                        placeholder="Ingresa Nº volante(s)"
                        TagInputWidth
                      />
                    </TemplateInputContainer>
                  </WFormControl>
                </div>
                <div className="p-col-12 p-md-3">
                  <WFormControl name="tci_nums">
                    <TemplateInputContainer label="Nro Serie(s) Factura(s):">
                      <WMultipleTagInputAlphaNum
                        height="5rem"
                        alphaNum
                        numInvoice
                        placeholder="Ingresa Nº serie(s) factura(s)"
                      />
                    </TemplateInputContainer>
                  </WFormControl>
                </div>
                <div className="p-col-12 p-md-2">
                  <WFormControl
                    name="from_date"
                    errorMessages={{
                      required: "*Campo requerido",
                    }}
                  >
                    <TemplateInputContainer
                      label="Fecha cita desde:"
                      color="#08082B"
                    >
                      <WCalendar
                        width="100%"
                        dateFormat={DD_MM_YY}
                        maxDate={new Date()}
                      />
                    </TemplateInputContainer>
                  </WFormControl>
                </div>
                <div className="p-col-12 p-md-2">
                  <WFormControl
                    name="to_date"
                    errorMessages={{
                      required: "*Campo requerido",
                    }}
                  >
                    <TemplateInputContainer
                      label="Fecha cita hasta:"
                      color="#08082B"
                    >
                      <WCalendar
                        width="100%"
                        maxDate={new Date()}
                        dateFormat={DD_MM_YY}
                      />
                    </TemplateInputContainer>
                  </WFormControl>
                </div>
              </>
            )}
          </Authorization>
          {trackingReportSelected && (
            <>
              <div className="p-col-12 p-md-7">
                <WFormControl name="volante_nums">
                  <TemplateInputContainer label="Números de volante:">
                    <WMultipleTagInput
                      width="100%"
                      placeholder="Ingresa Nº volante(s)"
                      TagInputWidth
                    />
                  </TemplateInputContainer>
                </WFormControl>
              </div>
            </>
          )}

          <Authorization
            actions={[
              REPORT_GET_VOLANTES_REPORT,
              REPORT_GET_APPOINTMENTS_REPORT,
            ]}
          >
            <div className="p-col-12 p-md-1">
              <div style={{ marginBottom: "1.8rem", height: "1.4rem" }} />
              <ButtonReload
                type="button"
                height="5rem"
                backgroundColor="#002E6C"
                onClick={() => {
                  const form = formConfig.form;
                  form.patchValue({
                    agent_ruc: "",
                    num_dam: "",
                    volante_nums: [],
                    id_pedidos: [],
                    tci_nums: [],
                    from_date: undefined,
                    to_date: undefined,
                    year_dam: new Date().getFullYear().toString(),
                    regimen_dam: "10",
                  });
                  formConfig.reset();
                  reportChangeState(reportType);
                }}
              >
                <img src={Reload} alt="reload" className="icon-reload" />
              </ButtonReload>
            </div>
            <div className="p-col-12 p-md-1">
              <div style={{ marginBottom: "1.8rem", height: "1.4rem" }} />
              <SaveButton
                width="100%"
                height="5rem"
                backgroundColor="#002E6C"
                iconRight={IconVectorRightBlue}
              />
            </div>
          </Authorization>
        </div>
      </WForm>
      {showSidebarDownload && (
        <AdminReportSidebar
          onOutsideClick={() => {
            setShowSidebarDownload(false);
          }}
          captureErrorSidebar={captureErrorSidebar}
        />
      )}

      <ModalGenerateReport
        showProcessing={showModalCreateReport}
        onAccept={() => {
          createReportExecutor.execute({
            ...formConfig.form.value,
          });
          setShowModalCreateReport(false);
        }}
        onClose={() => {
          setShowModalCreateReport(false);
        }}
      />

      <ModalProcessingTalma
        showProcessing={createReportExecutor.status === PROCESSING}
      />

      <ModalReportGenerate
        showProcessing={showModalReportGenerate}
        onChangeState={onChangeState}
      />

      {showMessageError && (
        <ConfirmationModal
          message={showMessageError}
          onAccept={() => {
            setShowMessageError("");
          }}
          title={`Atención`}
          toggleModal={() => {
            setShowMessageError("");
          }}
        />
      )}
    </div>
  );
};
